import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faSearch } from '@fortawesome/pro-regular-svg-icons'
import AbiTradePartnerApiUrls from '../../../apiUrls/AbiTradePartnerApiUrls'

const TradePartnerLookupDialog = ({
  open,
  onClose,
  onSelect,
  title = 'Party Lookup',
  initialSearchText = ''
}) => {
    const [searchTerm, setSearchTerm] = useState(initialSearchText)
  const [partners, setPartners] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const dialogRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (open) {
      setSearchTerm(initialSearchText)
      fetchTradePartners(0, rowsPerPage, initialSearchText)
    }
  }, [open, initialSearchText])

  const fetchTradePartners = async (pageNum, pageSize, search) => {
    setLoading(true)
    try {
      const response = await fetch(AbiTradePartnerApiUrls.LIST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify({
          page: pageNum,
          size: pageSize,
          search: search ?? '',
          partyMasterName: '',
          partyCode: '',
          name: '',
          partyIdentifier: '',
          city: '',
          countryCode: '',
          contactName: '',
        })
      })

      if (response.ok) {
        const json = await response.json()
        setPartners(json?.data?.content || [])
        setTotalElements(json?.data?.totalElements || 0)
      } else {
        setPartners([])
        setTotalElements(0)
      }
    } catch (error) {
      console.error('Error fetching trade partners:', error)
      setPartners([])
      setTotalElements(0)
    } finally {
      setLoading(false)
    }
  }

  const handleRowClick = (partner) => {
    setSelectedPartner(partner)
  }

  const handleSelect = () => {
    if (selectedPartner && typeof onSelect === 'function') {
      onSelect(selectedPartner)
    }
    handleClose()
  }

    const handleClose = () => {
    setSearchTerm('')
    setSelectedPartner(null)
    setPage(0)
    setPartners([])
    setTotalElements(0)
    if (typeof onClose === 'function') {
      onClose()
    }
  }

    const handleChangePage = (event, newPage) => {
    setPage(newPage)
    fetchTradePartners(newPage, rowsPerPage, searchTerm)
  }

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10)
    setRowsPerPage(newSize)
    setPage(0)
    fetchTradePartners(0, newSize, searchTerm)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      ref={dialogRef}
      onKeyDown={handleKeyDown}
      aria-labelledby="trade-partner-lookup-title"
    >
      <DialogTitle
        id="trade-partner-lookup-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {title}
        <IconButton
          aria-label="Close lookup dialog"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <FontAwesomeIcon icon={faClose} width="1em" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search by name, party code, identifier, city..."
                    value={searchTerm}
          onChange={(e) => {
            const value = e.target.value
            setSearchTerm(value)
            setPage(0)
            clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              fetchTradePartners(0, rowsPerPage, value)
            }, 500)
          }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faSearch} />
              </InputAdornment>
            )
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              )
            }
          }}
        />

        {/* Results Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Profile Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Party Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                                {partners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm ? 'No matching parties found' : 'No parties available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  partners.map((partner) => (
                    <TableRow
                      key={partner.id}
                      hover
                      selected={selectedPartner?.id === partner.id}
                      onClick={() => handleRowClick(partner)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{partner.partyMasterName || '-'}</TableCell>
                      <TableCell>{partner.name || '-'}</TableCell>
                      <TableCell>{partner.partyCode || '-'}</TableCell>
                      <TableCell>{partner.countryCode || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
                {totalElements > 0 && (
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          disabled={!selectedPartner}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TradePartnerLookupDialog
